import { NgTemplateOutlet } from '@angular/common';
import { Component, inject, input, output } from '@angular/core';
import { CancellationDocumentType } from 'src/app/_enums/cancellation/cancellation-document-type.enum';
import { isCancellationFlowOver } from 'src/app/_enums/cancellation/cancellation-flow-status.enum';
import { getCancellationSendMethodLabel } from 'src/app/_enums/cancellation/cancellation-send-method.enum';
import { getInsuranceTypeLabel } from 'src/app/_enums/insurance-type.enum';
import { EMPTY_VALUE, fmtDate } from 'src/app/_helpers/utils';
import { CancellationFlowDetailsResponse } from 'src/app/_types/cancellation.types';
import { CardComponent } from 'src/app/shared/ui-kit/card/card.component';
import { ExpansionPanelComponent } from 'src/app/shared/ui-kit/expansion-panel/expansion-panel.component';

import { CancellationDetailsService } from './cancellation-details.service';

/**
 * « Informations de la demande de résiliation »: the policy on the left, the
 * insurer on the right.
 *
 * The insurer's address is one string on the aggregate, so the design's city,
 * postcode and country rows would be permanent dashes; its email and phone
 * take their place. The letter date is read off the timeline, as the day the
 * letter was generated.
 */
@Component({
  selector: 'app-cancellation-request-info',
  standalone: true,
  imports: [CardComponent, ExpansionPanelComponent, NgTemplateOutlet],
  templateUrl: './cancellation-request-info.component.html',
  styleUrl: './cancellation-request-info.component.css',
})
export class CancellationRequestInfoComponent {
  private readonly detailsService = inject(CancellationDetailsService);

  /** The cards alone, for a host whose own panel holds them and offers « Modifier ». */
  readonly bare = input(false);

  /** « Modifier »: the host owns the form, as it owns every other sheet. */
  readonly edit = output<CancellationFlowDetailsResponse>();

  protected onEdit(): void {
    const { details } = this;
    if (details && this.editable) this.edit.emit(details);
  }

  /**
   * Correctable while the cancellation is still running. A customer's own request
   * opens too: what they stated is locked field by field in the form, but what
   * they left empty has to be filled before anything can move.
   */
  protected get editable(): boolean {
    const { details } = this;
    return !!details && !isCancellationFlowOver(details.status);
  }

  protected get details(): CancellationFlowDetailsResponse | null {
    return this.detailsService.state.details;
  }

  protected get insuranceTypeLabel(): string {
    const type = this.details?.insuranceType;
    return type ? getInsuranceTypeLabel(type) : EMPTY_VALUE;
  }

  protected get sendMethodLabel(): string {
    const method = this.details?.sendMethod;
    return method ? getCancellationSendMethodLabel(method) : EMPTY_VALUE;
  }

  protected get letterDate(): string {
    const document =
      this.details?.documents?.find(
        (x) => x.type === CancellationDocumentType.SIGNED_CANCELLATION_LETTER
      ) ?? this.details?.documents?.[0];
    return fmtDate(document?.letterDate);
  }

  protected get cancellationDate(): string {
    return fmtDate(this.details?.cancellationDate);
  }

  /**
   * Shown once set: when the cancellation is finalised, or carried over from
   * the cancellation date on requests filed before the field existed.
   */
  protected get effectiveCancellationDate(): string | null {
    const date = this.details?.effectiveCancellationDate;
    return date ? fmtDate(date) : null;
  }

  protected value(value: string | null | undefined): string {
    return value?.trim() || EMPTY_VALUE;
  }
}
